import React, { useState, useCallback } from "react";

import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Box,
  Typography,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseIcon from "@mui/icons-material/Close";
import DirectionsCarOutlinedIcon from "@mui/icons-material/DirectionsCarOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import AttachmentUploader from "./AttachmentUploader"; // import your uploader component


import { formatTimeToHHMM } from "../../utils/dateUtils";

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */
const formatDateDMY = (dateStr) => {
  if (!dateStr) return "";

  if (dateStr.includes("T")) {
    // Convert ISO UTC to local date
    const d = new Date(dateStr);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    // Handle plain yyyy-MM-dd format
    const [yyyy, mm, dd] = dateStr.split("-");
    return `${dd}-${mm}-${yyyy}`;
  }

  return dateStr;
};

const calculateTotalDays = (openDate, closeDate) => {
  if (!openDate || !closeDate) return "";
  const open = new Date(openDate);
  const close = new Date(closeDate);
  const diffTime = close.getTime() - open.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1;
  return diffDays > 0 ? diffDays : 1;
};

const calculateTotalHours = (openDate, openTime, closeDate, closeTime) => {
  if (!openDate || !openTime || !closeDate || !closeTime) return "";
  const open = new Date(openDate);
  const close = new Date(closeDate);

  const [openH, openM] = openTime.split(":").map(Number);
  const [closeH, closeM] = closeTime.split(":").map(Number);

  open.setHours(openH, openM, 0, 0);
  close.setHours(closeH, closeM, 0, 0);

  let diffMs = close.getTime() - open.getTime();
  if (diffMs < 0) diffMs = 0;
  const diffHours = diffMs / (1000 * 60 * 60);
  return diffHours.toFixed(2);
};

const calculateTotalKms = (openKm, closeKm) => {
  if (typeof openKm !== "number" || typeof closeKm !== "number") return "";
  const total = closeKm - openKm;
  return total >= 0 ? total : "";
};

const DUTY_LABELS = {
  Local: "Local",
  Outstation: "Outstation",
  EmployeeTransfer: "Employee Transfer",
  AirportTransfer: "Airport Transfer",
  Contract: "Monthly Contract",
  Package: "Package",
  OSDayRate: "OS Day Rate",
  KmSlab: "Km Slab",
};

/* ------------------------------------------------------------------ */
/* Design tokens (same as the booking form)                            */
/* ------------------------------------------------------------------ */

const C = {
  primary: "#0f6f8f",
  primaryHover: "#0b5872",
  primarySoft: "#e6f3f7",
  surface: "#f5f8fb",
  border: "#d8e2e9",
  borderStrong: "#b9cbd6",
  text: "#172b3a",
  muted: "#526575",
  headerText: "#334b5a",
  rowHover: "#edf7fa",
  openBg: "#eaf5fb",
  openText: "#17658a",
  closeBg: "#fff4e6",
  closeText: "#a65b10",
  totalBg: "#eaf7f0",
  totalText: "#18734a",
};

const btnBase = {
  textTransform: "none",
  fontSize: 14,
  fontWeight: 650,
  borderRadius: "8px",
  boxShadow: "none",
  minWidth: 0,
  px: 2.25,
  height: 36,
  transition: "background-color .15s ease, box-shadow .15s ease",
};

const primaryBtn = {
  ...btnBase,
  backgroundColor: C.primary,
  "&:hover": { backgroundColor: C.primaryHover, boxShadow: "none" },
};

// vertical divider that separates the Open / Close / Totals groups
const groupDivider = { borderLeft: `1px solid ${C.border}` };

const thBase = {
  fontSize: 12,
  fontWeight: 750,
  letterSpacing: "0.025em",
  color: C.headerText,
  whiteSpace: "nowrap",
  backgroundColor: "#f3f7fa",
  borderBottom: `1px solid ${C.border}`,
  px: 1.5,
  py: "2px",
  lineHeight: 1.3,
  textAlign: "center",
};

const tdBase = {
  fontSize: 13,
  fontWeight: 500,
  color: C.text,
  borderBottom: `1px solid ${C.border}`,
  px: 1.5,
  py: 1,
  textAlign: "center",
  fontVariantNumeric: "tabular-nums",
  whiteSpace: "nowrap",
};

const IconBtn = ({ children, sx, ...props }) => (
  <IconButton
    size="small"
    sx={{
      p: 0.75,
      width: 31,
      height: 31,
      borderRadius: "9px",
      border: "1px solid transparent",
      transition: "all .15s ease",
      "& svg": { fontSize: 17 },
      "&:hover": {
        backgroundColor: C.primarySoft,
        borderColor: "#c6e1e9",
        transform: "translateY(-1px)",
      },
      ...sx,
    }}
    {...props}
  >
    {children}
  </IconButton>
);

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */
export default function DutyTable({
  duties = [],
  vehicles = [],
  vehicleGroups = [],
  vendors = [],
  editDuty,
  deleteDuty,
  clientId,
  tripsheetId,
  setDuties, // <-- Make sure this is here
}) {
  const getDutyId = (duty) =>
    duty?.trip_duty_id ?? duty?.tripDutyId ?? duty?.id ?? null;

  const getVehicleNumber = (duty) => {
    // ============================
    // Vendor Vehicle
    // ============================
    if (duty?.is_vendor_tripsheet) {
      const vendor = vendors.find(
        (v) => Number(v.vendorid) === Number(duty.vendorid),
      );

      return (
        <Box>
          {/* Vendor Name */}
          <Box sx={{ fontSize: 13, fontWeight: 750, color: C.text, lineHeight: 1.35 }}>
            {vendor?.vendorname || ""}
          </Box>

          {/* Vendor Vehicle Number */}
          <Box sx={{ fontSize: 12, fontWeight: 550, color: C.muted, mt: 0.25, lineHeight: 1.3 }}>
            {duty?.vendor_vehicle_no || ""}
          </Box>
        </Box>
      );
    }

    // ============================
    // Normal Vehicle
    // ============================
    const vehicle = vehicles.find(
      (v) => Number(v.vehicle_id) === Number(duty?.vehicleid),
    );

    return vehicle?.vehicle_number || "";
  };

  const getVehicleGroupName = (groupId) => {
    const group = vehicleGroups.find((g) => g.categoryid === groupId);
    return group ? group.categoryname : groupId || "";
  };

  const [attachmentDialogOpen, setAttachmentDialogOpen] = useState(false);
  const [currentDutyId, setCurrentDutyId] = useState(null);

const openAttachmentsModal = (dutyId) => {
  console.log("Opening attachment dialog:", {
    clientId,
    tripsheetId,
    dutyId,
  });

  setCurrentDutyId(dutyId);
  setAttachmentDialogOpen(true);
};


  const closeAttachmentsModal = () => {
    setAttachmentDialogOpen(false);
    setCurrentDutyId(null);
  };

  const currentDuty =
    duties.find((duty) => String(getDutyId(duty)) === String(currentDutyId)) ||
    {};

  const setAttachmentsForCurrentDuty = useCallback(
    (value) => {
      if (typeof setDuties !== "function") {
        console.error("DutyTable: setDuties prop is missing.");
        return;
      }
      setDuties((previousDuties) =>
        previousDuties.map((duty) => {
          if (String(getDutyId(duty)) !== String(currentDutyId)) return duty;
          // supports both setAttachments([...]) and setAttachments(prev => [...])
          const next =
            typeof value === "function" ? value(duty.attachments || []) : value;
          return { ...duty, attachments: Array.isArray(next) ? next : [] };
        }),
      );
    },
    [setDuties, currentDutyId],
  );

  const COLS = 13;

  return (
    <Box
      sx={{
        border: `1px solid ${C.border}`,
        borderRadius: "14px",
        backgroundColor: "#fff",
        boxShadow: "0 5px 20px rgba(22, 48, 66, 0.07)",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2.5,
          py: 1.25,
          minHeight: 56,
          borderBottom: `1px solid ${C.border}`,
          background: "linear-gradient(110deg, #ffffff 0%, #f3f9fb 100%)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
          <Box sx={{
            width: 36, height: 36, borderRadius: "11px",
            display: "grid", placeItems: "center",
            bgcolor: C.primarySoft, color: C.primary,
            border: "1px solid #d2e8ef",
          }}>
            <DirectionsCarOutlinedIcon sx={{ fontSize: 20 }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: 15, fontWeight: 800, color: C.text, lineHeight: 1.25 }}>
              Duties
            </Typography>
            <Typography sx={{ fontSize: 11.5, color: C.muted, mt: 0.25 }}>
              Vehicle, duty timings and running totals
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            px: 1.5,
            py: 0.6,
            borderRadius: "999px",
            fontSize: 12,
            fontWeight: 800,
            color: C.primary,
            backgroundColor: C.primarySoft,
            border: "1px solid #c8e3eb",
            lineHeight: 1.25,
            whiteSpace: "nowrap",
          }}
        >
          {duties.length} {duties.length === 1 ? "duty" : "duties"}
        </Box>
      </Box>

      <Box sx={{ overflowX: "auto" }}>
        <Table
          sx={{ borderCollapse: "separate", borderSpacing: 0, minWidth: 1100 }}
        >
          <TableHead>
            <TableRow>
              <TableCell
                rowSpan={2}
                sx={{ ...thBase, textAlign: "left", pl: 2.5 }}
              >
                Vehicle no
              </TableCell>
              <TableCell rowSpan={2} sx={{ ...thBase, textAlign: "left" }}>
                Vehicle group
              </TableCell>
              <TableCell rowSpan={2} sx={{ ...thBase, textAlign: "left" }}>
                Duty type
              </TableCell>

              <TableCell
                colSpan={3}
                sx={{ ...thBase, ...groupDivider, color: C.openText, backgroundColor: C.openBg, py: 1 }}
              >
                <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
                  <ScheduleOutlinedIcon sx={{ fontSize: 15 }} /> Open
                </Box>
              </TableCell>
              <TableCell
                colSpan={3}
                sx={{ ...thBase, ...groupDivider, color: C.closeText, backgroundColor: C.closeBg, py: 1 }}
              >
                <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
                  <ScheduleOutlinedIcon sx={{ fontSize: 15 }} /> Close
                </Box>
              </TableCell>
              <TableCell
                colSpan={3}
                sx={{ ...thBase, ...groupDivider, color: C.totalText, backgroundColor: C.totalBg, py: 1, px: 0.75, width: 156 }}
              >
                <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
                  <ReceiptLongOutlinedIcon sx={{ fontSize: 15 }} /> Totals
                </Box>
              </TableCell>
              <TableCell
                rowSpan={2}
                sx={{
                  ...thBase,
                  ...groupDivider,
                  minWidth: 184,
                  width: 184,
                  px: 1.5,
                  position: "relative",
                }}
              >
                Actions
              </TableCell>
            </TableRow>
            <TableRow>
              {["Date", "Time", "Km"].map((label, i) => (
                <TableCell
                  key={"open-" + i}
                  sx={{ ...thBase, ...(i === 0 ? groupDivider : {}), backgroundColor: "#f8fafc", py: "2px" }}
                >
                  {label}
                </TableCell>
              ))}
              {["Date", "Time", "Km"].map((label, i) => (
                <TableCell
                  key={"close-" + i}
                  sx={{ ...thBase, ...(i === 0 ? groupDivider : {}), backgroundColor: "#f8fafc", py: "2px" }}
                >
                  {label}
                </TableCell>
              ))}
              {["Days", "Hours", "Kms"].map((label, i) => (
                <TableCell
                  key={"total-" + i}
                  sx={{ ...thBase, ...(i === 0 ? groupDivider : {}), backgroundColor: "#f8fafc", py: "2px" }}
                >
                  {label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {duties.length > 0 ? (
              duties.map((duty, idx) => {
                const openKmNum = Number(duty.open_km_reading);
                const closeKmNum = Number(duty.close_km_reading);
                const attachmentCount = Array.isArray(duty.attachments)
                  ? duty.attachments.length
                  : 0;

                return (
                  <TableRow
                    key={idx}
                    sx={{
                      height: 52,
                      backgroundColor: idx % 2 === 0 ? "#ffffff" : "#fbfdfe",
                      transition: "background-color .15s ease",
                      "&:hover": { backgroundColor: C.rowHover },
                      "&:hover td": { borderBottomColor: C.borderStrong },
                      "&:last-of-type td": { borderBottom: "none" },
                      "& td": { boxSizing: "border-box" },
                    }}
                  >
                    <TableCell
                      sx={{
                        ...tdBase,
                        textAlign: "left",
                        fontWeight: 500,
                        pl: 2.5,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                        <Box sx={{
                          width: 30, height: 30, flex: "0 0 30px",
                          display: "grid", placeItems: "center",
                          borderRadius: "8px", bgcolor: "#eef6f9",
                          color: C.primary, border: "1px solid #d9eaf0",
                        }}>
                          <DirectionsCarOutlinedIcon sx={{ fontSize: 17 }} />
                        </Box>
                        <Box sx={{ minWidth: 0, overflowWrap: "anywhere" }}>
                          {getVehicleNumber(duty)}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ ...tdBase, textAlign: "left" }}>
                      <Box sx={{
                        display: "inline-flex", alignItems: "center",
                        maxWidth: "100%", px: 1, py: 0.5,
                        borderRadius: "7px", bgcolor: "#f3f6f9",
                        border: "1px solid #e2e8ee", color: "#42586a",
                        fontSize: 12, fontWeight: 650, whiteSpace: "normal",
                      }}>
                        {getVehicleGroupName(duty.assigned_vehicle_group_id)}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ ...tdBase, textAlign: "left" }}>
                      <Box sx={{
                        display: "inline-flex", alignItems: "center",
                        maxWidth: "100%", px: 1, py: 0.5,
                        borderRadius: "7px", bgcolor: C.primarySoft,
                        border: "1px solid #d1e8ef", color: C.primary,
                        fontSize: 12, fontWeight: 700, whiteSpace: "normal",
                      }}>
                        {DUTY_LABELS[duty.duty] || duty.duty}
                      </Box>
                    </TableCell>

                    <TableCell sx={{ ...tdBase, ...groupDivider }}>
                      {formatDateDMY(duty.open_date)}
                    </TableCell>
                    <TableCell sx={tdBase}>
                      {formatTimeToHHMM(duty.open_time) || ""}
                    </TableCell>
                    <TableCell sx={tdBase}>
                      {duty.open_km_reading || ""}
                    </TableCell>

                    <TableCell sx={{ ...tdBase, ...groupDivider }}>
                      {formatDateDMY(duty.close_date)}
                    </TableCell>
                    <TableCell sx={tdBase}>
                      {formatTimeToHHMM(duty.close_time) || ""}
                    </TableCell>
                    <TableCell sx={tdBase}>
                      {duty.close_km_reading || ""}
                    </TableCell>

                    <TableCell
                      sx={{ ...tdBase, ...groupDivider, fontWeight: 750, color: C.totalText, backgroundColor: "#fbfefc", px: 0.75, width: 48 }}
                    >
                      {calculateTotalDays(duty.open_date, duty.close_date)}
                    </TableCell>
                    <TableCell sx={{ ...tdBase, fontWeight: 750, color: C.totalText, backgroundColor: "#fbfefc", px: 0.75, width: 58 }}>
                      {calculateTotalHours(
                        duty.open_date,
                        duty.open_time,
                        duty.close_date,
                        duty.close_time,
                      )}
                    </TableCell>
                    <TableCell sx={{ ...tdBase, fontWeight: 750, color: C.totalText, backgroundColor: "#fbfefc", px: 0.75, width: 48 }}>
                      {calculateTotalKms(openKmNum, closeKmNum)}
                    </TableCell>

                    <TableCell
                      sx={{
                        ...tdBase,
                        ...groupDivider,
                        whiteSpace: "nowrap",
                        minWidth: 184,
                        width: 184,
                        px: 1.5,
                        overflow: "visible",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 1,
                          width: "100%",
                          minWidth: 0,
                          overflow: "visible",
                        }}
                      >
                      <IconBtn
                        color="primary"
                        onClick={() => editDuty(idx)}
                        title="Edit duty"
                      >
                        <EditIcon />
                      </IconBtn>
                      <IconBtn
                        color="error"
                        onClick={() => deleteDuty(idx)}
                        title="Delete duty"
                      >
                        <DeleteIcon />
                      </IconBtn>

                      <IconBtn
                        title="Manage attachments"
                        sx={{
                          color: C.muted,
                          flex: "0 0 31px",
                          overflow: "visible",
                          "& .MuiBadge-root": { overflow: "visible" },
                        }}
                        onClick={() => {
                          const dutyId = getDutyId(duty);

                          console.log("Attachment IDs:", {
                            clientId,
                            tripsheetId,
                            dutyId,
                          });

                          if (dutyId == null) {
                            alert(
                              "Please save the tripsheet first, then add attachments.",
                            );
                            return;
                          }

                          openAttachmentsModal(dutyId);
                        }}
                      >
                        <Badge
                          badgeContent={attachmentCount}
                          color="primary"
                          overlap="circular"
                          sx={{
                            overflow: "visible",
                            "& .MuiBadge-badge": {
                              fontSize: 10,
                              fontWeight: 800,
                              minWidth: 17,
                              height: 17,
                              px: 0.4,
                              right: -9,
                              top: -5,
                              transform: "scale(1)",
                              transformOrigin: "100% 0%",
                              zIndex: 2,
                              border: "2px solid #ffffff",
                            },
                            "& .MuiBadge-root": { overflow: "visible" },
                            "& .MuiSvgIcon-root": {
                              fontSize: 19,
                              color: C.primary,
                            },
                          }}
                        >
                          <AttachFileIcon sx={{ fontSize: 19, color: C.primary }} />
                        </Badge>
                      </IconBtn>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell
                  colSpan={COLS}
                  align="center"
                  sx={{
                    ...tdBase,
                    color: C.muted,
                    py: 3.5,
                    borderBottom: "none",
                  }}
                >
                  No duties added yet.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>

      {/* Dialog for attachments */}
      <Dialog
        open={attachmentDialogOpen}
        onClose={closeAttachmentsModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            overflow: "hidden",
            boxShadow: "0 20px 60px rgba(15, 35, 55, 0.24)",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 3,
            py: 1.5,
            borderBottom: `1px solid ${C.border}`,
            background: "linear-gradient(110deg, #ffffff 0%, #f3f9fb 100%)",
            fontSize: 15,
            fontWeight: 800,
            color: C.text,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            <Box sx={{
              width: 34, height: 34, borderRadius: "10px",
              display: "grid", placeItems: "center",
              bgcolor: C.primarySoft, color: C.primary,
            }}>
              <AttachFileIcon sx={{ fontSize: 19 }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: 15, fontWeight: 800, color: C.text, lineHeight: 1.25 }}>
                Manage attachments
              </Typography>
              <Typography sx={{ fontSize: 11.5, fontWeight: 500, color: C.muted, mt: 0.25 }}>
                View, upload and manage duty receipts
              </Typography>
            </Box>
          </Box>
          <IconButton
            size="small"
            onClick={closeAttachmentsModal}
            sx={{ color: C.muted }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3, pt: "20px !important", bgcolor: "#fbfdff" }}>
          
{attachmentDialogOpen && (
  <AttachmentUploader
    key={`${clientId}-${tripsheetId}-${currentDutyId}`}
    attachments={currentDuty.attachments || []}
    setAttachments={setAttachmentsForCurrentDuty}
    clientId={clientId}
    tripsheetId={tripsheetId}
    tripDutyId={currentDutyId}
  />
)}

        </DialogContent>
        <DialogActions
          sx={{ px: 3, py: 1.5, borderTop: `1px solid ${C.border}`, bgcolor: "#f7fafc" }}
        >
          <Button
            onClick={closeAttachmentsModal}
            variant="contained"
            sx={primaryBtn}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
